import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, Button, Paper, Alert } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { theme } from '../../../../theme';
import { access } from '../../../../api/user';
import { authBus } from '../../domain/authBus';

export default function AccessLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const nextPath = useMemo(() => {
    const next = searchParams.get('next');
    return next && next.startsWith('/') ? next : '/stories';
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!login.trim() || !password.trim()) {
      setErrorMsg('Please enter login and password.');
      return;
    }

    try {
      setLoading(true);

      await access({
        login: login.trim(),
        password: password.trim(),
      });

      authBus.emit('login');
      navigate(nextPath, { replace: true });
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setErrorMsg('Invalid username or password.');
      } else {
        setErrorMsg('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background:
          'radial-gradient(circle at top left, rgba(209, 76, 255, 0.18), transparent 35%), linear-gradient(180deg, #18131d 0%, #120f16 100%)',
        px: 2,
      }}
    >
      <Paper
        sx={{
          width: 420,
          p: 4,
          background: 'linear-gradient(145deg, rgba(42, 36, 48, 0.98), rgba(25, 21, 30, 0.98))',
          border: `1px solid ${theme.palette.custom.modalBorder}`,
          color: theme.palette.custom.modalText,
          borderRadius: 4,
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)',
        }}
      >
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(`/login?next=${encodeURIComponent(nextPath)}`)}
          sx={{ mb: 2, px: 0, color: theme.palette.text.secondary }}
        >
          Back to sign-in options
        </Button>

        <Typography variant="h5" sx={{ mb: 1.5 }}>
          Direct reader login
        </Typography>

        <Typography sx={{ mb: 3, color: theme.palette.text.secondary }}>
          Sign in with your reader account and continue to the page you selected.
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        <Box component="form" onSubmit={handleLogin}>
          <TextField
            fullWidth
            label="Email or username"
            variant="filled"
            sx={{ mb: 2 }}
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            disabled={loading}
          />

          <TextField
            fullWidth
            type="password"
            label="Password"
            variant="filled"
            sx={{ mb: 2.5 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.9,
              },
            }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
